import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Row, Col } from "antd"
import { DndContext, closestCenter } from "@dnd-kit/core"
import { arrayMove, SortableContext, rectSortingStrategy } from "@dnd-kit/sortable"
import SortableMediaItem from "./SortableMediaItem"
import styles from "./MediaGrid.module.css"

const MediaGrid = ({ mediaItems = [], onImageClick, withActions = false, onDelete, onCrop, onReorder, sameSize, showHeroImageLabel }) => {
  const [items, setItems] = useState(mediaItems)

  useEffect(() => {
    setItems(mediaItems)
  }, [mediaItems])

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        const newItems = arrayMove(items, oldIndex, newIndex)

        if (onReorder) {
          onReorder(newItems)
        }

        return newItems
      })
    }
  }

  if (items.length === 0) return null

  const gutter = [12, 8]
  const sameSizeGutter = [16, 16]

  if (items.length < 5 || sameSize) {
    return (
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={rectSortingStrategy}>
          <Row gutter={sameSize ? sameSizeGutter : gutter}>
            {items.map((media, index) => (
              <Col
                // Updated: Applying sameSize and singleImage classes to the Col
                className={`${styles.singleImage} ${sameSize ? styles.sameSize : ""}`}
                key={media.id}
                xl={6}
                md={8}
                span={12}
              >
                <SortableMediaItem
                  id={media.id}
                  key={media.id}
                  media={media}
                  onImageClick={onImageClick}
                  withActions={withActions}
                  onDelete={onDelete}
                  onCrop={onCrop}
                  // Removed: className was moved to the parent Col
                  showHeroImageLabel={showHeroImageLabel && index === 0}
                />
              </Col>
            ))}
          </Row>
        </SortableContext>
      </DndContext>
    )
  }

  const renderGrid = (startIndex) => (
    <>
      <Col className={styles.heroImage} span={12}>
        <SortableMediaItem
          id={items[startIndex].id}
          media={items[startIndex]}
          index={startIndex}
          onImageClick={onImageClick}
          withActions={withActions}
          onDelete={onDelete}
          onCrop={onCrop}
          showHeroImageLabel={showHeroImageLabel && startIndex === 0}
        />
      </Col>
      {items[startIndex + 1] && (
        <Col span={12}>
          <Row gutter={gutter}>
            {Array.from({ length: 4 }).map(
              (_, i) =>
                items[startIndex + i + 1] && (
                  <Col key={items[startIndex + i + 1].id} span={12} className={styles.thumbnailImage}>
                    <SortableMediaItem
                      id={items[startIndex + i + 1].id}
                      media={items[startIndex + i + 1]}
                      index={startIndex + i + 1}
                      onImageClick={onImageClick}
                      withActions={withActions}
                      onDelete={onDelete}
                      onCrop={onCrop}
                      showHeroImageLabel={false}
                    />
                  </Col>
                )
            )}
          </Row>
        </Col>
      )}
      {items[startIndex + 5] && (
        <Col className={styles.heroImage} span={12}>
          <SortableMediaItem
            id={items[startIndex + 5].id}
            media={items[startIndex + 5]}
            index={startIndex + 5}
            onImageClick={onImageClick}
            withActions={withActions}
            onDelete={onDelete}
            onCrop={onCrop}
            showHeroImageLabel={false}
          />
        </Col>
      )}
      {items[startIndex + 6] && (
        <Col className={styles.heroImage} span={12}>
          <SortableMediaItem
            id={items[startIndex + 6].id}
            media={items[startIndex + 6]}
            index={startIndex + 6}
            onImageClick={onImageClick}
            withActions={withActions}
            onDelete={onDelete}
            onCrop={onCrop}
            showHeroImageLabel={false}
          />
        </Col>
      )}
      {Array.from({ length: 4 }).map(
        (_, i) =>
          items[startIndex + 7 + i] && (
            <Col key={i} span={6} className={styles.thumbnailImage}>
              <SortableMediaItem
                id={items[startIndex + 7 + i].id}
                media={items[startIndex + 7 + i]}
                index={startIndex + 7 + i}
                onImageClick={onImageClick}
                withActions={withActions}
                onDelete={onDelete}
                onCrop={onCrop}
                showHeroImageLabel={false}
              />
            </Col>
          )
      )}
    </>
  )

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <Row gutter={gutter}>
          {Array.from({ length: Math.ceil(items.length / 11) }).map((_, i) => (
            <React.Fragment key={i}>{renderGrid(i * 11)}</React.Fragment>
          ))}
        </Row>
      </SortableContext>
    </DndContext>
  )
}

MediaGrid.propTypes = {
  mediaItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      placeholder_url: PropTypes.string
    })
  ).isRequired,
  onImageClick: PropTypes.func,
  withActions: PropTypes.bool,
  onDelete: PropTypes.func,
  onCrop: PropTypes.func,
  onReorder: PropTypes.func,
  sameSize: PropTypes.bool,
  showHeroImageLabel: PropTypes.bool
}

export { MediaGrid }
